import React from "react";
import { Box, Divider, IconButton, Tooltip } from "@mui/joy";
import { SClose, SEdit, SSave, SUndo, SView } from "../../../../../styles/StyledMUI";
import DarkTypography from "../../../../common/DarkTypography";
import IconButtonCTA from "../../../../common/IconButtonCTA";
import { colorTokens } from "../../../../../styles/globalStyles";

type FollowUpFormHeaderProps = {
  customerName?: string;
  trainingTitle?: string;
  isEditing: boolean;
  canEdit: boolean;
  isDirty: boolean;
  onEdit: () => void;
  onExitEdit: () => void;
  onSave: () => void;
  onCancelChanges: () => void;
  onClose: () => void;
  disabled?: boolean;
};

type ToolbarActionProps = {
  title: string;
  ariaLabel: string;
  ctaVariant: "accent" | "success" | "danger";
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
};

type ModeButtonProps = {
  title: string;
  ariaLabel: string;
  Icon: typeof SView;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
};

const toolbarDividerSx = { mx: { xs: 0.25, sm: 0.5 }, alignSelf: "stretch" };

function ToolbarAction({
  title,
  ariaLabel,
  ctaVariant,
  onClick,
  disabled,
  icon,
}: ToolbarActionProps) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButtonCTA
          ctaVariant={ctaVariant}
          aria-label={ariaLabel}
          onClick={onClick}
          disabled={disabled}
        >
          {icon}
        </IconButtonCTA>
      </span>
    </Tooltip>
  );
}

function ModeButton({ title, ariaLabel, Icon, active, onClick, disabled }: ModeButtonProps) {
  const iconColor = active ? colorTokens.accent.contrastText : "var(--joy-palette-neutral-500)";

  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="sm"
          variant="plain"
          aria-label={ariaLabel}
          aria-pressed={active}
          onClick={active ? undefined : onClick}
          disabled={disabled && !active}
          sx={{
            minWidth: 40,
            minHeight: 36,
            borderRadius: "6px",
            cursor: active ? "default" : "pointer",
            backgroundColor: active ? colorTokens.accent.primary : "transparent",
            "&:hover": {
              backgroundColor: active ? colorTokens.accent.primary : "neutral.100",
            },
          }}
        >
          <Icon
            sx={{
              fontSize: 20,
              color: iconColor,
              cursor: "inherit",
              "&:hover": { color: iconColor },
            }}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function FollowUpFormHeader({
  customerName,
  trainingTitle,
  isEditing,
  canEdit,
  isDirty,
  onEdit,
  onExitEdit,
  onSave,
  onCancelChanges,
  onClose,
  disabled = false,
}: FollowUpFormHeaderProps) {
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0.75, sm: 1 },
        px: "1rem",
        py: { xs: 1, sm: 1.5 },
        backgroundColor: colorTokens.bg.sheetLight,
        borderBottom: "1px solid #E0E0E0",
      }}
    >
      <DarkTypography
        level="h3"
        colorVariant="title"
        sx={{
          minWidth: 0,
          fontSize: { xs: "1rem", sm: "1.6rem" },
          overflowWrap: "anywhere",
        }}
      >
        {customerName || "Élève"} - {trainingTitle || "Formation"}
      </DarkTypography>

      <Box
        role="toolbar"
        aria-label="Actions de la fiche de suivi"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 1 },
          minWidth: 0,
        }}
      >
        {canEdit && (
          <>
            <Box
              role="group"
              aria-label="Mode"
              sx={{
                display: "flex",
                gap: 0.25,
                p: 0.25,
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
              }}
            >
              <ModeButton
                title="Lecture"
                ariaLabel="Passer en lecture"
                Icon={SView}
                active={!isEditing}
                onClick={onExitEdit}
                disabled={disabled}
              />
              <ModeButton
                title="Édition"
                ariaLabel="Passer en édition"
                Icon={SEdit}
                active={isEditing}
                onClick={onEdit}
                disabled={disabled}
              />
            </Box>
            <Divider orientation="vertical" sx={toolbarDividerSx} />
          </>
        )}

        {isEditing && (
          <>
            <ToolbarAction
              title="Enregistrer"
              ariaLabel="Enregistrer la fiche de suivi"
              ctaVariant="success"
              onClick={onSave}
              disabled={disabled || !isDirty}
              icon={<SSave />}
            />
            <ToolbarAction
              title="Annuler les modifications"
              ariaLabel="Annuler les modifications"
              ctaVariant="danger"
              onClick={onCancelChanges}
              disabled={disabled || !isDirty}
              icon={<SUndo />}
            />
          </>
        )}

        <Box sx={{ ml: "auto" }}>
          <ToolbarAction
            title="Fermer"
            ariaLabel="Fermer la fiche de suivi"
            ctaVariant="danger"
            onClick={onClose}
            disabled={disabled}
            icon={<SClose />}
          />
        </Box>
      </Box>
    </Box>
  );
}
